"use client";

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/api/use-auth";
import {
  useAuthModalStore,
  selectOpenLoginModal,
  selectIsOnboardingOpen,
  selectOpenOnboarding,
  selectCloseOnboarding,
} from "@/lib/store/auth-store";
import { useUsdcBalance } from "@/lib/hooks/use-token";
import { Skeleton } from "@/components/ui/skeleton";
import { User } from "lucide-react";
import { WalletInfo } from "@/components/layout/wallet-info";
import { AddFundsModal } from "@/components/layout/add-funds-modal";
import { AccountMenu } from "@/components/layout/account-menu";
import {
  OpenLineupsMenu,
  OpenLineupsMobile,
  useHasOpenLineups,
} from "@/components/entries/open-lineups-chip";
import { WelcomePopup } from "@/components/onboarding/welcome-popup";
import {
  CreatorActivityMenu,
  MonthlyRunMenu,
} from "@/components/creator-copy/header-chips";
import { WheelTakeover } from "@/components/wheel/takeover";
import { CardsTakeover } from "@/components/cards/takeover";
import { SlotTakeover } from "@/components/slot/takeover";
import { Switch } from "@/components/ui/switch";
import { useFirstTimeStore, selectFirstTimeEnabled, selectSetFirstTimeEnabled } from "@/lib/creator-copy/first-time";
import { QA_ENABLED } from "@/lib/creator-copy/flags";
import { usePlanCut, usePlanHome } from "@/lib/config/plan-minimum";

const ONBOARDING_STORAGE_KEY = "maginot:first-visit-onboarding-seen:v1";
const FirstVisitOnboarding = dynamic(
  () =>
    import("@/components/onboarding/first-visit-onboarding").then(
      (module) => module.FirstVisitOnboarding
    ),
  { ssr: false }
);

export function Header() {
  // Communities is its own product on a monochrome frame: a white wordmark in
  // the middle, the wallet beside the avatar, no lineups or Monthly Run chips.
  const communities = usePathname()?.startsWith("/communities") ?? false;
  // /simple frames the whole app in one centred width: the bar's ends line up with the board's.
  const framed = ["/simple", "/simplestart"].includes(usePathname() ?? "");
  const logoSrc = communities ? "/getpicks-logo-white.svg" : "/getpicks-logo.svg";
  // Board v3 shell, every page (lib/layout/shell.ts): no sidebar, so the
  // wordmark lives in this bar and the avatar opens the account menu, which
  // carries what the sidebar used to (wallet, entries, leaderboard, rewards,
  // invite, help).
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  // On /startplan (Plan Minimum) the logo stays in the plan and cut features are not mounted.
  const home = usePlanHome();
  const cutMonthlyRun = usePlanCut("monthlyRun");
  const cutRewardGames = usePlanCut("rewardGames");
  // The First time sign up switch, a prototype review control shown only
  // when QA_ENABLED. See lib/creator-copy/first-time.ts.
  const firstTime = useFirstTimeStore(selectFirstTimeEnabled);
  const setFirstTime = useFirstTimeStore(selectSetFirstTimeEnabled);
  const openLoginModal = useAuthModalStore(selectOpenLoginModal);
  const isOnboardingOpen = useAuthModalStore(selectIsOnboardingOpen);
  const openOnboarding = useAuthModalStore(selectOpenOnboarding);
  const closeOnboarding = useAuthModalStore(selectCloseOnboarding);
  const { data: balance } = useUsdcBalance();
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  // A player with money in play gets the lineups chip in the mobile header's
  // left cell; "How to play" returns the moment nothing is open.
  const hasOpenLineups = useHasOpenLineups();

  // Identity check moved to Profile; Deposit opens Add Funds directly.
  const handleDeposit = () => {
    setAddFundsOpen(true);
  };

  const formattedBalance = balance?.formatted ?? "0.00";
  const avatarSrc = user?.avatar_url
    ? `${user.avatar_url}?v=${new Date(user.updated_at).getTime()}`
    : "/images/def-avatar.jpg";
  const signOut = () => {
    // With the switch on, every sign in is the first one.
    if (firstTime) setFirstTime(true);
    void logout();
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isAuthenticated) {
      closeOnboarding();
      return;
    }

    // Auto-open on first visit is temporarily disabled — guide is only shown
    // when the user explicitly clicks the "How to play" button.
  }, [isAuthenticated, isLoading, openOnboarding, closeOnboarding]);

  const markOnboardingSeen = () => {
    window.localStorage.setItem(ONBOARDING_STORAGE_KEY, "1");
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-grey-700 bg-background lg:hidden">
      {/* Mobile header — hidden on desktop where sidebar takes over */}
      <div className="flex items-center justify-between px-3 sm:px-4 gap-2 h-[72px]">
        {/* Left: Auth-dependent */}
        {isLoading ? (
          <Skeleton className="h-8 w-20 rounded-full shrink-0" />
        ) : isAuthenticated ? (
          hasOpenLineups ? (
            <OpenLineupsMobile className="shrink-0" />
          ) : (
            <button
              onClick={openOnboarding}
              className="bg-primary text-white px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap shrink-0"
            >
              How to play
            </button>
          )
        ) : (
          // Guests get the product explainer instead — it links to the
          // how-to-play guide from inside.
          <button
            onClick={() => setWelcomeOpen(true)}
            className="bg-primary text-white px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap shrink-0"
          >
            New here?
          </button>
        )}

        {/* Center: GETPICKS logo */}
        <Link href={home} className="relative h-[13px] w-[108px] sm:h-[15px] sm:w-[124px] shrink-0">
          <Image
            src={logoSrc}
            alt="GETPICKS"
            fill
            sizes="(min-width: 640px) 124px, 108px"
            className="object-contain"
            priority
          />
        </Link>

        {/* Right: Auth-dependent */}
        {isLoading ? (
          <Skeleton className="size-8 rounded-full shrink-0" />
        ) : isAuthenticated ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm sm:text-base font-normal tracking-tight text-white whitespace-nowrap">
              {formattedBalance} USD.P
            </span>
            {/* On a phone the account menu is the door to the Reward Hub,
                Invite & earn, the wallet and help. */}
            <AccountMenu
              avatarSrc={avatarSrc}
              wallet={user?.embedded_wallet_address}
              balance={balance?.formatted ?? null}
              onSignOut={signOut}
            />
          </div>
        ) : (
          <button
            onClick={openLoginModal}
            className="flex items-center justify-center size-8 rounded-full bg-primary"
          >
            <User className="size-4 text-white" />
          </button>
        )}
      </div>

      </header>

      {/* Desktop top bar — hidden on mobile where header takes over */}
      <div className={cn("hidden lg:flex sticky top-0 z-50 items-center justify-end h-[66px] px-6 border-b border-grey-700 bg-background relative", framed && "px-[max(1.5rem,calc((100%-1280px)/2))]")}>
        {isLoading ? (
          <Skeleton className="h-9 w-32 rounded-full" />
        ) : isAuthenticated && user?.embedded_wallet_address && communities ? (
          <>
            <Link href={home} className="absolute left-1/2 top-1/2 block h-[19px] w-[167px] -translate-x-1/2 -translate-y-1/2">
              <Image src={logoSrc} alt="GETPICKS" fill sizes="167px" className="object-contain" priority />
            </Link>
            <div className="flex items-center gap-3">
              <CreatorActivityMenu />
              <WalletInfo balance={balance?.formatted ?? null} onDeposit={handleDeposit} />
              <div className="h-6 w-px bg-grey-600" />
              <AccountMenu
                avatarSrc={avatarSrc}
                wallet={user.embedded_wallet_address}
                balance={balance?.formatted ?? null}
                onSignOut={signOut}
              />
            </div>
          </>
        ) : isAuthenticated && user?.embedded_wallet_address ? (
          <>
            {/* Left: the wordmark, then open lineups at a glance. The chip
                collapses when nothing is open. */}
            <div className="mr-auto flex items-center gap-6">
              <Link href={home} className="relative block h-[19px] w-[167px] shrink-0">
                <Image src={logoSrc} alt="GETPICKS" fill sizes="167px" className="object-contain" priority />
              </Link>
              {!framed && <OpenLineupsMenu />}
            </div>
            {/* Balance + Deposit unit — absolutely centered in the bar */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <WalletInfo balance={balance?.formatted ?? null} onDeposit={handleDeposit} />
            </div>
            {/* Right cluster */}
            <div className="flex items-center gap-3">
              {/* What happened to you, and the Monthly Run ladder with the
                  reward games it opens. The Reward Hub lives in the account
                  menu, so it has no chip here. */}
              {/* /simple: the bets chip sits here, in place of the notifications. */}
              {framed ? <OpenLineupsMenu align="right" /> : <CreatorActivityMenu />}
              {!cutMonthlyRun && !framed && <MonthlyRunMenu />}
              {!cutRewardGames && (
                <>
                  <WheelTakeover />
                  <CardsTakeover />
                  <SlotTakeover />
                </>
              )}
              <div className="h-6 w-px bg-grey-600" />
              {/* The avatar opens the account. */}
              <AccountMenu
                avatarSrc={avatarSrc}
                wallet={user.embedded_wallet_address}
                balance={balance?.formatted ?? null}
                onSignOut={signOut}
              />
            </div>
          </>
        ) : (
          <>
          <Link
            href={home}
            className={cn(
              "block h-[19px] w-[167px] shrink-0",
              communities ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : "relative mr-auto"
            )}
          >
            <Image src={logoSrc} alt="GETPICKS" fill sizes="167px" className="object-contain" priority />
          </Link>
          <div className="flex items-center gap-2">
            {QA_ENABLED && (
              <label className="mr-2 flex cursor-pointer items-center gap-2.5 font-open-sauce text-[13px] font-semibold tracking-tight text-grey-300">
                First time sign up
                <Switch size="sm" checked={firstTime} onCheckedChange={setFirstTime} />
              </label>
            )}
            <button
              onClick={() => setWelcomeOpen(true)}
              className="bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold"
            >
              New here?
            </button>
            <button
              onClick={openLoginModal}
              className="bg-grey-800 text-white px-4 py-2 rounded-full text-sm font-semibold"
            >
              Sign in
            </button>
          </div>
          </>
        )}
      </div>

      {!isLoading && isOnboardingOpen && (
        <FirstVisitOnboarding
          open={isOnboardingOpen}
          onOpenChange={(open: boolean) => { if (open) openOnboarding(); else closeOnboarding(); }}
          onComplete={markOnboardingSeen}
          onLoginClick={openLoginModal}
        />
      )}

      <WelcomePopup
        open={welcomeOpen}
        onOpenChange={setWelcomeOpen}
        onLoginClick={() => {
          setWelcomeOpen(false);
          openLoginModal();
        }}
        onHowToPlayClick={() => {
          setWelcomeOpen(false);
          openOnboarding();
        }}
      />

      <AddFundsModal open={addFundsOpen} onOpenChange={setAddFundsOpen} />
    </>
  );
}
